import { useCallback, useEffect, useRef, useState, useContext } from 'react'
import { UserContext } from '@/components/auth/auth'
import { getCsrfToken } from '@/components/utils/csrf'

// VAPID public key from environment
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || ''

interface PushSubscribePayload {
  endpoint: string
  p256dh: string
  auth: string
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function usePushNotifications() {
  const user = useContext(UserContext)
  const [permission, setPermission] = useState<NotificationPermission | null>(null)
  const [isSupported, setIsSupported] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const initializedRef = useRef(false)

  // Check browser support
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true)
    }
  }, [])

  // Check existing permission on mount
  useEffect(() => {
    if (!('Notification' in window)) {
      setPermission('denied')
      return
    }
    setPermission(Notification.permission)
  }, [])

  const subscribeToPush = useCallback(async () => {
    if (!isSupported || !user.authorized) return

    try {
      const registration = await navigator.serviceWorker.ready
      if (!registration.pushManager) {
        setError('Push manager not available')
        return
      }

      // Check existing subscription
      const existingSub = await registration.pushManager.getSubscription()
      if (existingSub) {
        setIsSubscribed(true)
        return
      }

      // Subscribe with VAPID key
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: VAPID_PUBLIC_KEY ? urlBase64ToUint8Array(VAPID_PUBLIC_KEY) : (null as any),
      })

      // Send subscription to backend
      const subscriptionData: PushSubscribePayload = {
        endpoint: sub.endpoint,
        p256dh: arrayBufferToBase64(sub.getKey('p256dh')!),
        auth: arrayBufferToBase64(sub.getKey('auth')!),
      }

      const response = await fetch('/api/push/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrftoken': getCsrfToken(),
        },
        body: JSON.stringify(subscriptionData),
      })

      if (response.ok) {
        setIsSubscribed(true)
      } else {
        const data = await response.json().catch(() => ({}))
        setError(data.message || 'Failed to subscribe to push notifications')
      }
    } catch (err) {
      console.error('Push subscription error:', err)
      // User may have blocked permissions, don't set error state
      if ((err as Error).message?.includes('Permission denied')) {
        setPermission('denied')
      } else {
        setError('Failed to subscribe to push notifications')
      }
    }
  }, [isSupported, user.authorized])

  const unsubscribeFromPush = useCallback(async () => {
    if (!isSupported) return

    try {
      const registration = await navigator.serviceWorker.ready
      const sub = await registration.pushManager.getSubscription()
      if (sub) {
        const subscriptionData: PushSubscribePayload = {
          endpoint: sub.endpoint,
          p256dh: arrayBufferToBase64(sub.getKey('p256dh')!),
          auth: arrayBufferToBase64(sub.getKey('auth')!),
        }

        await fetch('/api/push/push/unsubscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Csrftoken': getCsrfToken(),
          },
          body: JSON.stringify(subscriptionData),
        })

        await sub.unsubscribe()
        setIsSubscribed(false)
      }
    } catch (err) {
      console.error('Push unsubscribe error:', err)
    }
  }, [isSupported])

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      setPermission('denied')
      return
    }

    try {
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm === 'granted') {
        await subscribeToPush()
      }
    } catch (err) {
      console.error('Notification permission error:', err)
      setPermission('denied')
    }
  }, [subscribeToPush])

  // Auto-request permission when:
  // 1. User is authorized
  // 2. App is installed as PWA (standalone mode)
  // 3. Permission not yet denied
  // 4. VAPID public key is configured
  useEffect(() => {
    if (initializedRef.current) return
    if (!user.authorized) return
    if (!isSupported) return
    if (permission === 'denied') return
    if (!VAPID_PUBLIC_KEY) return

    // Check if PWA is installed
    const isInstalled =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true

    if (isInstalled) {
      initializedRef.current = true
      requestPermission()
    }
  }, [user.authorized, isSupported, permission, requestPermission])

  // Re-subscribe when user becomes authorized (e.g., after login)
  useEffect(() => {
    if (user.authorized && permission === 'granted') {
      subscribeToPush()
    }
  }, [user.authorized, permission, subscribeToPush])

  return {
    isSupported,
    permission,
    isSubscribed,
    error,
    requestPermission,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush,
  }
}
